import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, Heart, Utensils, Upload, FileText, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IntakeForm = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading, valid, submitted, error
  const [prefill, setPrefill] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", date_of_birth: "", phone: "", email: "", location: "",
    preferred_channel: "whatsapp", guardian_contact: "",
    weight: "", height: "", blood_pressure: "", fasting_glucose: "",
    lipid_profile: "", other_lab_results: "", measurements_unavailable: "",
    main_health_concerns: "", personal_goals: "", reason_for_support: "",
    previous_nutrition_interventions: "",
    service_consent: false, doctor_sharing_consent: false, marketing_consent: false,
    doctor_name: "", doctor_email: "", doctor_practice: "", doctor_phone: "",
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await fetch(`${API}/intake/${token}`);
      if (res.ok) {
        const data = await res.json();
        setPrefill(data);
        setForm(prev => ({
          ...prev,
          full_name: data.patient_name || "",
          email: data.patient_email || "",
          phone: data.patient_phone || "",
        }));
        setStatus("valid");
      } else {
        const err = await res.json();
        toast.error(err.detail || "This form link is no longer valid.");
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (uploadedFiles.length + files.length > 10) {
      toast.error("You can upload a maximum of 10 documents.");
      return;
    }

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds the 10MB limit.`);
        continue;
      }

      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await fetch(`${API}/intake/${token}/upload-document`, {
          method: "POST",
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setUploadedFiles(prev => [...prev, { id: data.document_id, name: file.name, size: file.size }]);
          toast.success(`${file.name} uploaded.`);
        } else {
          const err = await res.json();
          toast.error(err.detail || `Failed to upload ${file.name}.`);
        }
      } catch {
        toast.error(`Could not upload ${file.name}. Please try again.`);
      } finally {
        setUploading(false);
      }
    }
    e.target.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name) { toast.error("Please enter your full name."); return; }
    if (!form.service_consent) { toast.error("Please acknowledge the service consent to continue."); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/intake/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus("submitted");
      } else {
        const err = await res.json();
        toast.error(err.detail || "There was a problem submitting your form.");
      }
    } catch {
      toast.error("Unable to submit. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-stone-900 mb-2">Form unavailable</h1>
          <p className="text-stone-500">This form link may have expired or been already submitted. Please contact your dietitian for a new link.</p>
        </div>
      </div>
    );
  }

  if (status === "submitted") {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <div className="text-center max-w-md">
          <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-stone-900 mb-2">Thank you</h1>
          <p className="text-stone-500 leading-relaxed">Your pre-consultation form has been submitted successfully. Your dietitian will review it before your appointment. You do not need to take any further action right now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="intake-form">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 h-16 flex items-center">
          <img src="/platemind-logo-new.png" alt="PlateMind" className="h-7 w-auto" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Pre-consultation form</h1>
          <p className="mt-2 text-base text-stone-500 leading-relaxed">
            Please complete this form before your consultation. It helps your dietitian prepare for your appointment. If you do not have some of the information requested, you can leave those fields empty. Your dietitian can help you fill in any gaps during your session.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* === SECTION 1: Demographics === */}
          <SectionCard title="Your details" description="Basic contact and personal information.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Your full name" className="rounded-lg" data-testid="intake-name" />
              </Field>
              <Field label="Date of birth">
                <Input type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label="Phone number">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+234..." className="rounded-lg" />
              </Field>
              <Field label="Email address">
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" className="rounded-lg" />
              </Field>
              <Field label="Location">
                <Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="City, Country" className="rounded-lg" />
              </Field>
              <Field label="Preferred communication channel">
                <Select value={form.preferred_channel} onValueChange={(v) => update("preferred_channel", v)}>
                  <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Guardian or caregiver contact (if applicable)">
              <Input value={form.guardian_contact} onChange={(e) => update("guardian_contact", e.target.value)} placeholder="Name, phone or email" className="rounded-lg" />
            </Field>
          </SectionCard>

          {/* === SECTION 2: Measurements === */}
          <SectionCard title="Health measurements" description="If you have any recent measurements, please share them below. It is fine to leave these empty if you do not have them. Your dietitian can record them during your consultation.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Weight (kg)">
                <Input value={form.weight} onChange={(e) => update("weight", e.target.value)} placeholder="e.g. 78" className="rounded-lg" />
              </Field>
              <Field label="Height (cm)">
                <Input value={form.height} onChange={(e) => update("height", e.target.value)} placeholder="e.g. 170" className="rounded-lg" />
              </Field>
              <Field label="Blood pressure (mmHg)">
                <Input value={form.blood_pressure} onChange={(e) => update("blood_pressure", e.target.value)} placeholder="e.g. 130/85" className="rounded-lg" />
              </Field>
              <Field label="Fasting blood glucose (mmol/L)">
                <Input value={form.fasting_glucose} onChange={(e) => update("fasting_glucose", e.target.value)} placeholder="e.g. 5.6" className="rounded-lg" />
              </Field>
            </div>
            <Field label="Lipid profile (cholesterol, if available)">
              <Input value={form.lipid_profile} onChange={(e) => update("lipid_profile", e.target.value)} placeholder="Total cholesterol, LDL, HDL, triglycerides" className="rounded-lg" />
            </Field>
            <Field label="Other laboratory results">
              <Textarea value={form.other_lab_results} onChange={(e) => update("other_lab_results", e.target.value)} placeholder="Any other recent test results you would like to share" className="rounded-lg" rows={2} />
            </Field>
            <Field label="Measurements not available">
              <Input value={form.measurements_unavailable} onChange={(e) => update("measurements_unavailable", e.target.value)} placeholder="Note any measurements you do not currently have" className="rounded-lg" />
            </Field>
          </SectionCard>

          {/* === SECTION 3: Reason === */}
          <SectionCard title="Reason for consultation" description="Help your dietitian understand why you are seeking support.">
            <Field label="Main health concerns">
              <Textarea value={form.main_health_concerns} onChange={(e) => update("main_health_concerns", e.target.value)} placeholder="What health concerns would you like to address?" className="rounded-lg" rows={3} />
            </Field>
            <Field label="Your goals">
              <Textarea value={form.personal_goals} onChange={(e) => update("personal_goals", e.target.value)} placeholder="What would you like to achieve through this programme?" className="rounded-lg" rows={2} />
            </Field>
            <Field label="Why are you seeking nutrition support?">
              <Textarea value={form.reason_for_support} onChange={(e) => update("reason_for_support", e.target.value)} placeholder="What prompted you to look into nutritional support at this time?" className="rounded-lg" rows={2} />
            </Field>
            <Field label="Previous nutrition interventions">
              <Textarea value={form.previous_nutrition_interventions} onChange={(e) => update("previous_nutrition_interventions", e.target.value)} placeholder="Have you previously worked with a dietitian or followed a specific diet? If so, please describe briefly." className="rounded-lg" rows={2} />
            </Field>
          </SectionCard>

          {/* === SECTION 3.5: Medical Documents === */}
          <SectionCard title="Medical documents" description="If you have any recent medical reports, lab results or relevant documents, you can upload them here. Up to 10 files, each under 10MB. Accepted formats: PDF, images, Word documents.">
            <div className="space-y-3">
              <label
                htmlFor="file-upload"
                className="flex items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-lg px-4 py-6 cursor-pointer hover:border-[#FF7A00]/50 hover:bg-[#FF7A00]/5 transition-colors"
                data-testid="intake-file-upload"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#FF7A00]" />
                ) : (
                  <Upload className="w-5 h-5 text-stone-400" />
                )}
                <span className="text-sm text-stone-500">
                  {uploading ? "Uploading..." : "Click to select files or drag and drop"}
                </span>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt,.csv,.xls,.xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading || uploadedFiles.length >= 10}
                />
              </label>

              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  {uploadedFiles.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 bg-stone-50 rounded-lg px-3 py-2">
                      <FileText className="w-4 h-4 text-[#FF7A00] shrink-0" />
                      <span className="text-sm text-stone-700 flex-1 truncate">{f.name}</span>
                      <span className="text-xs text-stone-400">{(f.size / 1024).toFixed(0)} KB</span>
                    </div>
                  ))}
                  <p className="text-xs text-stone-400">{uploadedFiles.length}/10 documents uploaded</p>
                </div>
              )}
            </div>
          </SectionCard>

          {/* === SECTION 4: Doctor sharing === */}
          <SectionCard title="Doctor communication" description="If you would like your dietitian to share progress reports with your doctor, please provide their details below. This is optional.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Doctor's name">
                <Input value={form.doctor_name} onChange={(e) => update("doctor_name", e.target.value)} placeholder="Dr..." className="rounded-lg" />
              </Field>
              <Field label="Doctor's email">
                <Input type="email" value={form.doctor_email} onChange={(e) => update("doctor_email", e.target.value)} placeholder="doctor@practice.com" className="rounded-lg" />
              </Field>
              <Field label="Practice or institution">
                <Input value={form.doctor_practice} onChange={(e) => update("doctor_practice", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label="Doctor's phone">
                <Input value={form.doctor_phone} onChange={(e) => update("doctor_phone", e.target.value)} className="rounded-lg" />
              </Field>
            </div>
          </SectionCard>

          {/* === SECTION 5: Consent === */}
          <SectionCard title="Consent" description="Please read and acknowledge the following.">
            <div className="space-y-4">
              <ConsentItem
                checked={form.service_consent}
                onChange={(v) => update("service_consent", v)}
                required
                label="I consent to PlateMind processing the information I have provided for the purpose of delivering the cardiometabolic nutrition programme, including clinical assessment, nutrition planning and care coordination."
                testId="consent-service"
              />
              <ConsentItem
                checked={form.doctor_sharing_consent}
                onChange={(v) => update("doctor_sharing_consent", v)}
                label="I consent to PlateMind sharing relevant clinical progress reports with the doctor I have nominated above. I understand I can withdraw this consent at any time."
                testId="consent-doctor"
              />
              <ConsentItem
                checked={form.marketing_consent}
                onChange={(v) => update("marketing_consent", v)}
                label="I would like to receive occasional newsletters and health resources from PlateMind. (This is optional and separate from the service.)"
                testId="consent-marketing"
              />
            </div>
          </SectionCard>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="submit" disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 px-8 text-base font-semibold" data-testid="intake-submit">
              {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</> : "Submit form"}
            </Button>
            <p className="text-xs text-stone-400 self-center">
              Your information is stored securely and processed in accordance with our privacy policy.
            </p>
          </div>
        </form>
      </main>
    </div>
  );
};

// Helper components
const SectionCard = ({ title, description, children }) => (
  <Card className="rounded-xl border-stone-200/60 p-5 sm:p-6 space-y-4">
    <div className="mb-2">
      <h2 className="text-lg font-bold text-stone-900">{title}</h2>
      {description && <p className="text-sm text-stone-500 mt-0.5">{description}</p>}
    </div>
    {children}
  </Card>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-stone-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const ConsentItem = ({ checked, onChange, label, required, testId }) => (
  <div className="flex items-start gap-3">
    <Checkbox
      checked={checked}
      onCheckedChange={onChange}
      className="mt-0.5 data-[state=checked]:bg-[#FF7A00] data-[state=checked]:border-[#FF7A00]"
      data-testid={testId}
    />
    <p className="text-sm text-stone-600 leading-relaxed">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </p>
  </div>
);

export default IntakeForm;
